// apps/client/src/ui-kit/Toggle.tsx
//
// iOS-style toggle switch — used for legal acceptance and settings.
// Purple active state matches the Beelucky aesthetic.
//

import { ReactNode } from 'react';
import { motion } from 'framer-motion';

interface ToggleProps {
    checked: boolean;
    onChange: (checked: boolean) => void;
    label?: ReactNode;
    description?: ReactNode;
    disabled?: boolean;
    error?: boolean;
}

export function Toggle({ checked, onChange, label, description, disabled = false, error = false }: ToggleProps) {
    return (
        <button
            type="button"
            onClick={() => !disabled && onChange(!checked)}
            disabled={disabled}
            className={`
                flex items-start gap-3 w-full p-4 rounded-3xl text-left transition-all
                ${error ? 'bg-orange-500/10 border border-orange-400/30' : 'bg-white/95 border border-white/30'}
                ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer active:scale-[0.98]'}
            `}
        >
            {/* Toggle track */}
            <div
                className={`
                    relative flex-shrink-0 w-12 h-7 rounded-full mt-0.5 transition-colors duration-200
                    ${checked ? 'bg-[#6D28D9]' : 'bg-gray-300'}
                `}
            >
                {/* Toggle knob */}
                <motion.div
                    className="absolute top-0.5 w-6 h-6 rounded-full bg-white shadow-md"
                    animate={{ x: checked ? 22 : 2 }}
                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                />
            </div>

            {/* Label + description */}
            {(label || description) && (
                <div className="flex-1 min-w-0">
                    {label && (
                        <span className={`font-bold text-sm block ${error ? 'text-orange-600' : 'text-[#1A0B2E]'}`}>
                            {label}
                        </span>
                    )}
                    {description && (
                        <p className="text-[#1A0B2E]/50 text-xs mt-1 leading-relaxed">
                            {description}
                        </p>
                    )}
                </div>
            )}
        </button>
    );
}
